import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Flex,
  Text,
  useToast,
  SimpleGrid,
  Avatar,
  Image,
} from "@chakra-ui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck } from "@fortawesome/free-solid-svg-icons";
import { cqAPI, HOST } from "../../lib/Auth";
import { useNavigate } from 'react-router-dom';
import moment from "moment"; // Assuming you use moment for date formatting

function Appointments() {
  const [appointments, setAppointments] = useState([]);
  const toast = useToast();
  const navigate = useNavigate();

  const fetchAppointments = async () => {
    try {
      const response = await cqAPI.get("/pp/appointments");
      if (response.type === "ok") {
        setAppointments(response.data);
      } else {
        toast({
          title: "Error fetching appointments",
          description: response.msg,
          status: "error",
        });
      }
    } catch (error) {
      console.error("Error fetching appointments:", error);
      toast({
        title: "Error fetching appointments",
        description: error.message,
        status: "error",
      });
    }
  };

  const confirmAppointment = async (appointmentId) => {
    try {
      const response = await cqAPI.get(`/appconfirm/${appointmentId}`);
      if (response.type === "ok") {
        toast({
          title: "Appointment confirmed successfully",
          status: "success",
        });
        fetchAppointments(); // Refresh the appointments list
      } else {
        toast({
          title: "Error confirming appointment",
          description: response.msg,
          status: "error",
        });
      }
    } catch (error) {
      console.error("Error confirming appointment:", error);
      toast({
        title: "Error confirming appointment",
        description: error.message,
        status: "error",
      });
    }
  };

  useEffect(() => {
    fetchAppointments();
  }, []);

  const getAppointmentDuration = (duration, start, end) => {
    return duration ?? Math.floor((new Date(end) - new Date(start)) / (1000 * 60))
  }

  return (
    <Box
      p={4}
      overflowY="auto"
      maxHeight="100vh"
      width="100%"
      position="relative"
    >
      <Box textAlign="right" mt={4} paddingBottom="25px">
        <Button
          onClick={() =>
            navigate({
              pathname: `/appointments/new`,
              search: location.search,
            })
          }
        >
          New Appointment
        </Button>
      </Box>
      <SimpleGrid columns={{ base: 1, md: 2, lg: 1 }}>
        {Array.isArray(appointments) &&
          appointments.map(
            ({
              _id,
              address,
              business,
              confirmed,
              starttime,
              endtime,
              duration,
              provider_name,
              pid,
            }) => (
              <Box
                key={_id}
                borderWidth="1px"
                borderRadius="lg"
                mb={4}
                width="100%"
                className="actionableItem"
              >
                <Flex
                  direction={{ base: "column", md: "row" }}
                  p={4}
                  justify="space-between"
                  align={{ base: "stretch", md: "center" }}
                  height="100%"
                >
                  {/* Block 1: Provider Picture and Name */}
                  <Flex
                    direction="column"
                    align="center"
                    minWidth="80px"
                    mb={{ base: 4, md: 0 }}
                  >
                    {pid ? (
                      <Image
                        borderRadius="full"
                        width="80px"
                        height="80px"
                        src={`${HOST}/profileimage?uid=${pid}`}
                      />
                    ) : (
                      <Avatar
                        name={provider_name}
                        size="lg"
                        width="80px"
                        height="80px"
                      />
                    )}
                    <Text fontSize="sm" mt={2}>
                      <b>{provider_name}</b>
                    </Text>
                  </Flex>

                  {/* Block 2: Business Information */}
                  <Box
                    textAlign="left"
                    ml={{ base: 0, md: 4 }}
                    mb={{ base: 4, md: 0 }}
                    minWidth={{ base: "100%", md: "200px" }}
                  >
                    <Text fontSize="sm">
                      <b>{business}</b>
                    </Text>
                    <Text fontSize="sm">
                      {address.line1}
                      <br />
                      {address.line2}
                      {address.line2 && <br />}
                      {address.city}
                      <br />
                      {address.zip}
                    </Text>
                  </Box>

                  {/* Block 3: Appointment Time and Duration */}
                  <Box
                    textAlign="left"
                    ml={{ base: 0, md: 4 }}
                    mb={{ base: 4, md: 0 }}
                    minWidth={{ base: "100%", md: "200px" }}
                  >
                    <Text fontSize="sm">
                      {moment(starttime).format("MMMM Do YYYY, h:mm:ss a")}
                    </Text>
                    <Text fontSize="sm"> {getAppointmentDuration(duration, starttime, endtime)} minutes</Text>
                  </Box>

                  {/* Block 4: Confirmation Status */}
                  <Box textAlign="center" minWidth="120px">
                    {confirmed ? (
                      <Text fontSize="sm" color="green.500">
                        <b>Confirmed</b>
                      </Text>
                    ) : _id.includes(":") ? (
                      <Button
                        colorScheme="blue"
                        leftIcon={<FontAwesomeIcon icon={faCheck} />}
                        onClick={() => confirmAppointment(_id)}
                      >
                        Confirm
                      </Button>
                    ) : (
                      <Text fontSize="sm" color="green.500">
                        <b>Awaiting Office Confirmation</b>
                      </Text>
                    )}
                  </Box>
                </Flex>
              </Box>
            )
          )}
      </SimpleGrid>
    </Box>
  );
}

export default Appointments;
